import { Button } from 'components/Button/Button'
import { HandbookItem, HandbookItemType } from 'types'
import { stringType } from '../util'
import { useCallback, useState } from 'react'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { Modal, useModals } from 'almanac-ui/Modal'

export interface DeleteItemConfirmationProps {
  onDeleteNested: () => Promise<void>
  onDeleteAndReparent: () => Promise<void>
  item: HandbookItem
}

export const DeleteItemConfirmation = observer(
  ({ onDeleteNested, onDeleteAndReparent, item }: DeleteItemConfirmationProps) => {
    const handbooks = useRootStore().handbooks
    const treeItem = handbooks.items.get(item.id.toString())
    const handbookWithTree = treeItem?.parentHandbook
    const handbook = handbookWithTree?.handbook
    const [deleting, setDeleting] = useState<boolean>(false)
    const isSection = item.type === HandbookItemType.Section
    const { closeModal } = useModals()

    const handleClose = useCallback(() => {
      closeModal?.()
    }, [closeModal])

    const doDelete = async (deleteFn: () => Promise<void>) => {
      setDeleting(true)
      await deleteFn()
      setDeleting(false)
      handleClose()
    }

    const removeFromHandbookText = () => {
      return `This will remove the doc from this ${
        handbook?.folder?.handbook_mode ? 'handbook' : 'folder'
      } and will not delete it. You can still find the doc in recents and search.`
    }

    const DocumentModalContent = () => (
      <>
        <Modal.Header title={`Remove ${item.title}`} />
        <Modal.Content>
          {handbook?.folder && !item.mention_id
            ? `This will delete the ${stringType(item.type)}${
                item.type === HandbookItemType.NestedHandbook ? ' and all its contents' : ''
              }. You will not be able to access it anymore.`
            : removeFromHandbookText()}
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Button onPress={closeModal} size="xl" variant="tertiary">
              Cancel
            </Button>
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            <Button size="lg" onPress={() => doDelete(onDeleteNested)} isDisabled={deleting}>
              Remove
            </Button>
          </Modal.FooterContent>
        </Modal.Footer>
      </>
    )

    const NestingItemModalContent = () => (
      <>
        <Modal.Header title={`Remove ${stringType(item.type)} ${item.title}`} />
        <Modal.Content>
          {handbook?.folder && `This will remove the ${stringType(item.type)} and all its contents.`}
          {isSection && `What do you want to do with the items nested inside this ${stringType(item.type)}?`}
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot={!item?.folder ? 'left' : 'right'}>
            <Button size="lg" variant={isSection ? 'secondary' : 'primary'} onPress={() => doDelete(onDeleteNested)}>
              {handbook?.folder && 'Remove everything'}
              {!handbook?.folder && 'Remove all nested items'}
            </Button>
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            {!item?.folder && (
              <Button size="lg" onPress={() => doDelete(onDeleteAndReparent)} isDisabled={deleting}>
                Remove only the {stringType(item.type)}
              </Button>
            )}
          </Modal.FooterContent>
        </Modal.Footer>
      </>
    )

    const ModalContent = treeItem?.hasChildren ? NestingItemModalContent : DocumentModalContent

    return (
      <Modal.Root onClose={closeModal}>
        <ModalContent />
      </Modal.Root>
    )
  },
)
